/**
 * PageViewers — Shows OTHER users viewing the same page.
 * Reads from PresenceContext (no extra API calls).
 * Renders nothing when the user is alone on the page.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { usePresence } from '../contexts/PresenceContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const PAGE_NAMES = {
  '/': 'Parts Catalogue',
  '/parts': 'Parts Catalogue',
  '/taxonomy': 'Taxonomy',
  '/inventory': 'Inventory',
  '/inventory/receiving': 'GRN Management',
  '/inventory/stock': 'Stock Inquiry',
  '/inventory/locations': 'Location Management',
  '/inventory/lots': 'Lot Management',
  '/inventory/issue-transfer': 'Issue/Transfer',
  '/inventory/cycle-counts': 'Cycle Counts',
  '/inventory/quarantine': 'Quarantine',
  '/inventory/ncr': 'NCR Management',
  '/inventory/adjustments': 'Adjustments',
  '/inventory/qc-dashboard': 'QC Dashboard',
  '/inventory/manager-dashboard': 'Manager Dashboard',
  '/admin/user': 'User Management',
  '/admin/permissions': 'Role Permissions',
  '/settings': 'Settings',
  '/settings/profile': 'Profile Settings',
  '/db-explorer': 'DB Explorer',
};

const getPageName = (pathname) => {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname.startsWith('/parts/')) return 'Part Details';
  if (pathname.startsWith('/admin/user/')) return 'User Details';
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 0
    ? segments[segments.length - 1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : 'Dashboard';
};

const getInitials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const formatRole = (role) =>
  role ? role.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '';

const AvatarDot = ({ user }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        className="w-5 h-5 rounded-full bg-[#101957] border border-blue-200 flex items-center justify-center text-[9px] font-bold text-white cursor-default hover:scale-110 hover:z-10 transition-transform"
        data-testid={`page-viewer-dot-${user.user_id || user.display_name}`}
      >
        {getInitials(user.display_name || user.full_name)}
      </div>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="text-xs">
      <p className="font-semibold">{user.display_name || user.full_name}</p>
      {user.role && <p className="text-slate-500">{formatRole(user.role)}</p>}
    </TooltipContent>
  </Tooltip>
);

const PageViewers = () => {
  const { onlineUsers } = usePresence();
  const { user } = useAuth();
  const location = useLocation();

  const myPage = getPageName(location.pathname);
  const myId = user?.user_id || user?.id;

  const others = onlineUsers.filter(
    (u) => u.current_page === myPage && u.user_id !== myId
  );

  if (others.length === 0) return null;

  const firstName = others[0].display_name || others[0].full_name;
  const label =
    others.length === 1
      ? `${firstName} is also viewing this page`
      : `${firstName} and ${others.length - 1} other${others.length > 2 ? 's' : ''} are also viewing this page`;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex items-center justify-center gap-2.5 px-4 py-1.5 bg-blue-50 border-b border-blue-100 text-blue-700 text-xs font-medium flex-shrink-0"
        data-testid="page-viewers-indicator"
      >
        <Eye className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
        <div className="flex -space-x-1.5">
          {others.slice(0, 5).map((u) => (
            <AvatarDot key={u.user_id} user={u} />
          ))}
        </div>
        <span>{label}</span>
      </div>
    </TooltipProvider>
  );
};

export default PageViewers;
